import React from 'react';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';
import { toast } from 'react-hot-toast';
import { 
  XMarkIcon, 
  ClipboardDocumentListIcon,
  UserIcon,
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  WrenchScrewdriverIcon,
  CogIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  TruckIcon,
  PencilSquareIcon,
  TrashIcon,
  PrinterIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { useCustomers } from '../../hooks/useCustomers';
import { useWorksheets } from '../../hooks/useWorksheets';
import { Worksheet } from '../../types/worksheet';
import { Button } from '../common/Button';
import Modal from '../common/Modal';
import DigitalSignature from '../common/DigitalSignature';

interface WorksheetViewProps {
  worksheet: Worksheet;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: (worksheet: Worksheet) => void;
}

const WorksheetView: React.FC<WorksheetViewProps> = ({
  worksheet: initialWorksheet,
  isOpen,
  onClose,
  onEdit
}) => {
  const { t } = useLanguage();
  const { customers } = useCustomers();
  const { addSignature, removeSignature, worksheets } = useWorksheets();
  const [isSignatureModalOpen, setIsSignatureModalOpen] = React.useState(false);
  
  // Use real-time data from the hook instead of static prop
  const worksheet = worksheets.find(w => w.id === initialWorksheet.id) || initialWorksheet;

  const formatDate = (date: any) => {
    if (!date) return '';
    
    try {
      if (date && typeof date.toDate === 'function') {
        return format(date.toDate(), 'yyyy. MM. dd.', { locale: hu });
      }
      
      if (date instanceof Date && !isNaN(date.getTime())) {
        return format(date, 'yyyy. MM. dd.', { locale: hu });
      }
      
      if (typeof date === 'string') {
        const parsedDate = new Date(date);
        if (!isNaN(parsedDate.getTime())) {
          return format(parsedDate, 'yyyy. MM. dd.', { locale: hu });
        }
      }
      
      return '';
    } catch (error) {
      return '';
    }
  };

  const formatDateTime = (date: any) => {
    if (!date) return '';
    
    try {
      if (date && typeof date.toDate === 'function') {
        return format(date.toDate(), 'yyyy. MM. dd. HH:mm:ss', { locale: hu });
      }
      
      if (date instanceof Date && !isNaN(date.getTime())) {
        return format(date, 'yyyy. MM. dd. HH:mm:ss', { locale: hu });
      }
      
      if (typeof date === 'string') {
        const parsedDate = new Date(date);
        if (!isNaN(parsedDate.getTime())) {
          return format(parsedDate, 'yyyy. MM. dd. HH:mm:ss', { locale: hu });
        }
      }
      
      return '';
    } catch (error) {
      return '';
    }
  };

  const formatTime = (time: string | undefined) => {
    if (!time) return '';
    return time;
  };

  const getCustomerName = (customerId: string) => {
    const customer = customers.find(c => c.id === customerId);
    if (!customer) return 'Ismeretlen ügyfél';
    
    return customer.type === 'organization' 
      ? customer.companyName 
      : `${customer.firstName} ${customer.lastName}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-500';
      case 'in_progress': return 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-700';
      case 'completed': return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700';
      case 'cancelled': return 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700';
      default: return 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-500';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700';
      case 'medium': return 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-700';
      case 'low': return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700';
      default: return 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-500';
    }
  };

  const getWorkTypeIcon = (type: string) => {
    switch (type) {
      case 'installation': return <WrenchScrewdriverIcon className="h-4 w-4" />;
      case 'maintenance': return <CogIcon className="h-4 w-4" />;
      case 'repair': return <WrenchScrewdriverIcon className="h-4 w-4" />;
      case 'inspection': return <MagnifyingGlassIcon className="h-4 w-4" />;
      default: return <ClipboardDocumentListIcon className="h-4 w-4" />;
    }
  };

  const getWorkTypeColor = (type: string) => {
    switch (type) {
      case 'installation': return 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-700';
      case 'maintenance': return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700';
      case 'repair': return 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700';
      case 'inspection': return 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-300 border-purple-200 dark:border-purple-700';
      default: return 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-500';
    }
  };

  const getStatusColorHex = (status: string) => {
    switch (status) {
      case 'draft': return '#6b7280';
      case 'in_progress': return '#2563eb';
      case 'completed': return '#059669';
      case 'cancelled': return '#dc2626';
      default: return '#6b7280';
    }
  };

  const getPriorityColorHex = (priority: string) => {
    switch (priority) {
      case 'high': return '#dc2626';
      case 'medium': return '#d97706';
      case 'low': return '#059669';
      default: return '#6b7280';
    }
  };

  const getWorkTypeColorHex = (type: string) => {
    switch (type) {
      case 'installation': return '#2563eb';
      case 'maintenance': return '#059669';
      case 'repair': return '#dc2626';
      case 'inspection': return '#7c3aed';
      case 'other': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const handleSignature = async (signatureData: string, signerName: string, signerRole: string) => {
    try {
      // Get current location (optional)
      let location = 'Unknown location';
      if (navigator.geolocation) {
        try {
          const position = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
          });
          location = `${position.coords.latitude.toFixed(6)}, ${position.coords.longitude.toFixed(6)}`;
        } catch (geoError) {
          console.warn('Could not get location:', geoError);
        }
      }

      // Create new signature object
      const newSignature = {
        signedBy: 'current-user', // This should come from auth context
        signerName,
        signerRole,
        signatureData,
        signedAt: new Date(),
        location,
        deviceInfo: navigator.userAgent
      };

      // Add signature to worksheet in database
      await addSignature(worksheet.id, newSignature);
      
      toast.success(t('worksheets.messages.signatureAdded'));
      setIsSignatureModalOpen(false);
      
    } catch (error) {
      console.error('Error adding signature:', error);
      toast.error(t('worksheets.messages.signatureError'));
    }
  };

  const handleRemoveSignature = async (signatureId: string) => {
    try {
      await removeSignature(worksheet.id, signatureId);
      toast.success('Aláírás törölve');
    } catch (error) {
      console.error('Error removing signature:', error);
      toast.error('Hiba történt az aláírás törlésekor');
    }
  };

  const handlePrint = () => {
    // Create print content
    const printContent = `
      <!DOCTYPE html>
      <html lang="hu">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Munkalap - ${worksheet.worksheetNumber}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: 'Segoe UI', Arial, sans-serif;
              font-size: 11px;
              line-height: 1.2;
              color: #333;
              background: white;
              padding: 15px;
            }
            
            .print-container {
              max-width: 100%;
              margin: 0;
            }
            
            .header {
              text-align: center;
              margin-bottom: 15px;
              padding-bottom: 10px;
              border-bottom: 1px solid #e5e7eb;
            }
            
            .header h1 {
              font-size: 22px;
              color: #1e40af;
              margin-bottom: 5px;
              font-weight: bold;
            }
            
            .header .subtitle {
              font-size: 12px;
              color: #6b7280;
              margin-bottom: 8px;
            }
            
            .worksheet-number {
              font-size: 14px;
              font-weight: bold;
              color: #059669;
              background: #ecfdf5;
              padding: 4px 12px;
              border-radius: 6px;
              display: inline-block;
            }
            
            .section {
              margin-bottom: 12px;
              break-inside: avoid;
            }
            
            .section-header {
              font-size: 13px;
              font-weight: bold;
              color: #1e40af;
              margin-bottom: 6px;
              padding-bottom: 2px;
              border-bottom: 1px solid #e5e7eb;
            }
            
            .info-grid {
              display: grid;
              grid-template-columns: 1fr 1fr 1fr;
              gap: 8px;
              margin-bottom: 8px;
            }
            
            .info-item {
              display: flex;
              flex-direction: column;
              gap: 2px;
            }
            
            .info-label {
              font-size: 9px;
              color: #6b7280;
              font-weight: 500;
              text-transform: uppercase;
              letter-spacing: 0.3px;
            }
            
            .info-value {
              font-size: 11px;
              color: #1f2937;
              font-weight: 500;
            }
            
            .status-badge {
              display: inline-block;
              padding: 2px 8px;
              border-radius: 12px;
              font-size: 9px;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.3px;
            }
            
            .status-draft { background: #f3f4f6; color: #374151; }
            .status-in_progress { background: #dbeafe; color: #1e40af; }
            .status-completed { background: #d1fae5; color: #059669; }
            .status-cancelled { background: #fee2e2; color: #dc2626; }
            
            .priority-high { background: #fee2e2; color: #dc2626; }
            .priority-medium { background: #fef3c7; color: #d97706; }
            .priority-low { background: #d1fae5; color: #059669; }
            
            .type-installation { background: #dbeafe; color: #1e40af; }
            .type-maintenance { background: #d1fae5; color: #059669; }
            .type-repair { background: #fee2e2; color: #dc2626; }
            .type-inspection { background: #ede9fe; color: #7c3aed; }
            
            .description-box {
              background: #f9fafb;
              padding: 8px;
              border-radius: 4px;
              border: 1px solid #e5e7eb;
              margin-top: 5px;
              font-size: 11px;
              line-height: 1.3;
            }
            
            .signatures-section {
              margin-top: 15px;
            }
            
            .signature-item {
              border: 1px solid #e5e7eb;
              border-radius: 6px;
              padding: 8px;
              margin-bottom: 8px;
              background: #fafafa;
              display: flex;
              align-items: center;
              gap: 12px;
            }
            
            .signature-info {
              flex-grow: 1;
              font-size: 11px;
            }
            
            .signature-name {
              font-weight: bold;
              color: #1e40af;
              display: block;
            }
            
            .signature-role {
              color: #6b7280;
              display: block;
              font-size: 10px;
            }
            
            .signature-image {
              width: 120px;
              height: 40px;
              border: 1px solid #e5e7eb;
              border-radius: 3px;
              background: white;
              flex-shrink: 0;
            }
            
            .signature-date {
              font-size: 9px;
              color: #6b7280;
              display: block;
            }
            
            .footer {
              margin-top: 15px;
              padding-top: 10px;
              border-top: 1px solid #e5e7eb;
              display: flex;
              justify-content: space-between;
              align-items: center;
              font-size: 9px;
              color: #6b7280;
            }
            
            .footer .brand {
              font-weight: bold;
              color: #1e40af;
            }
            
            @media print {
              body {
                print-color-adjust: exact;
                -webkit-print-color-adjust: exact;
                font-size: 10px;
                padding: 0;
              }
              
              .print-container {
                padding: 0;
              }
              
              .section {
                break-inside: avoid;
                margin-bottom: 8px;
              }
              
              .signature-item {
                break-inside: avoid;
              }
              
              .info-grid {
                grid-template-columns: 1fr 1fr 1fr 1fr;
                gap: 6px;
              }
              
              @page {
                margin: 12mm;
                size: A4;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            <!-- Header -->
            <div class="header">
              <h1>MUNKALAP</h1>
              <div class="subtitle">YouCRM - Munkalap Rendszer</div>
              <div class="worksheet-number">
                ${worksheet.worksheetNumber}
              </div>
            </div>
            
            <!-- Basic Info -->
            <div class="section">
              <div class="section-header">Alapadatok</div>
              <div class="info-grid">
                <div class="info-item">
                  <div class="info-label">Cím</div>
                  <div class="info-value">${worksheet.title}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Ügyfél</div>
                  <div class="info-value">${getCustomerName(worksheet.customerId)}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Munka típusa</div>
                  <div class="info-value">
                    <span class="status-badge type-${worksheet.workType}">
                      ${t(`worksheets.workTypes.${worksheet.workType}`)}
                    </span>
                  </div>
                </div>
                <div class="info-item">
                  <div class="info-label">Prioritás</div>
                  <div class="info-value">
                    <span class="status-badge priority-${worksheet.priority}">
                      ${t(`worksheets.priority.${worksheet.priority}`)}
                    </span>
                  </div>
                </div>
                <div class="info-item">
                  <div class="info-label">Állapot</div>
                  <div class="info-value">
                    <span class="status-badge status-${worksheet.status}">
                      ${t(`worksheets.status.${worksheet.status}`)}
                    </span>
                  </div>
                </div>
                ${worksheet.location ? `
                <div class="info-item">
                  <div class="info-label">Helyszín</div>
                  <div class="info-value">${worksheet.location}</div>
                </div>
                ` : ''}
                ${worksheet.worksheetDate ? `
                <div class="info-item">
                  <div class="info-label">Munkalap dátuma</div>
                  <div class="info-value">${formatDate(worksheet.worksheetDate)}</div>
                </div>
                ` : ''}
                ${worksheet.dueDate ? `
                <div class="info-item">
                  <div class="info-label">Határidő</div>
                  <div class="info-value">${formatDate(worksheet.dueDate)}</div>
                </div>
                ` : ''}
              </div>
            </div>
            
            <!-- Work Details -->
            ${worksheet.description || worksheet.startTime || worksheet.endTime ? `
            <div class="section">
              <div class="section-header">Munka részletei</div>
              <div class="info-grid">
                ${worksheet.startTime && worksheet.endTime ? `
                <div class="info-item">
                  <div class="info-label">Munkaidő</div>
                  <div class="info-value">${formatTime(worksheet.startTime)} - ${formatTime(worksheet.endTime)}</div>
                </div>
                ` : ''}
              </div>
              ${worksheet.description ? `
              <div class="description-box">
                <strong>Leírás:</strong><br>
                ${worksheet.description}
              </div>
              ` : ''}
            </div>
            ` : ''}
            
            <!-- Travel Info -->
            ${worksheet.travelKmStart || worksheet.travelKmEnd || worksheet.travelKmTotal || worksheet.travelCosts ? `
            <div class="section">
              <div class="section-header">Utazási információk</div>
              <div class="info-grid">
                ${worksheet.travelKmStart ? `
                <div class="info-item">
                  <div class="info-label">Kezdő km</div>
                  <div class="info-value">${worksheet.travelKmStart} km</div>
                </div>
                ` : ''}
                ${worksheet.travelKmEnd ? `
                <div class="info-item">
                  <div class="info-label">Befejező km</div>
                  <div class="info-value">${worksheet.travelKmEnd} km</div>
                </div>
                ` : ''}
                ${worksheet.travelKmTotal ? `
                <div class="info-item">
                  <div class="info-label">Összesen</div>
                  <div class="info-value">${worksheet.travelKmTotal} km</div>
                </div>
                ` : ''}
                ${worksheet.travelCosts ? `
                <div class="info-item">
                  <div class="info-label">Utazási költségek</div>
                  <div class="info-value">${worksheet.travelCosts} Ft</div>
                </div>
                ` : ''}
              </div>
            </div>
            ` : ''}
            
            <!-- Deployment Info -->
            ${worksheet.deploymentOccurred ? `
            <div class="section">
              <div class="section-header">Kiszállási információk</div>
              <div class="info-grid">
                <div class="info-item">
                  <div class="info-label">Kiszállás</div>
                  <div class="info-value">
                    <span class="status-badge status-completed">Megtörtént</span>
                  </div>
                </div>
                ${worksheet.deploymentDate ? `
                <div class="info-item">
                  <div class="info-label">Kiszállás dátuma</div>
                  <div class="info-value">${formatDate(worksheet.deploymentDate)}</div>
                </div>
                ` : ''}
                ${worksheet.deploymentTime ? `
                <div class="info-item">
                  <div class="info-label">Kiszállás időpontja</div>
                  <div class="info-value">${formatTime(worksheet.deploymentTime)}</div>
                </div>
                ` : ''}
              </div>
            </div>
            ` : ''}
            
            <!-- Materials & Equipment -->
            ${worksheet.materials || worksheet.equipment ? `
            <div class="section">
              <div class="section-header">Anyagok és eszközök</div>
              ${worksheet.materials ? `
              <div class="description-box">
                <strong>Anyagok:</strong><br>
                ${worksheet.materials}
              </div>
              ` : ''}
              ${worksheet.equipment ? `
              <div class="description-box">
                <strong>Eszközök:</strong><br>
                ${worksheet.equipment}
              </div>
              ` : ''}
            </div>
            ` : ''}
            
            <!-- Safety Notes -->
            ${worksheet.safetyNotes ? `
            <div class="section">
              <div class="section-header">Biztonsági megjegyzések</div>
              <div class="description-box">
                ${worksheet.safetyNotes}
              </div>
            </div>
            ` : ''}
            
            <!-- Special Notes -->
            ${worksheet.specialNotes ? `
            <div class="section">
              <div class="section-header">Speciális megjegyzések</div>
              <div class="description-box">
                ${worksheet.specialNotes}
              </div>
            </div>
            ` : ''}
            
            <!-- Signatures -->
            ${worksheet.signatures && worksheet.signatures.length > 0 ? `
            <div class="section signatures-section">
              <div class="section-header">Aláírások</div>
              ${worksheet.signatures.map(signature => `
                <div class="signature-item">
                  <div class="signature-info">
                    <span class="signature-name">${signature.signerName}</span>
                    <span class="signature-role">${signature.signerRole}</span>
                    <span class="signature-date">${formatDateTime(signature.signedAt)}</span>
                  </div>
                  <img src="${signature.signatureData}" alt="Aláírás" class="signature-image">
                </div>
              `).join('')}
            </div>
            ` : ''}
            
            <!-- Footer -->
            <div class="footer">
              <div>Generálva: ${format(new Date(), 'yyyy. MM. dd. HH:mm:ss', { locale: hu })}</div>
              <div class="brand">YouCRM - Munkalap Rendszer</div>
            </div>
          </div>
        </body>
      </html>
    `;

    // Open print window
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      
      // Wait for content to load then print
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
        }, 500);
      };
    } else {
      toast.error('Popup blokkolva - engedélyezd a popup ablakokat a nyomtatáshoz');
    }
  };

  return (
    <>
      <Modal 
        isOpen={isOpen} 
        onClose={onClose} 
        title={t('worksheets.view')}
        size="4xl"
      >
        <div className="p-4">
          {/* Enhanced Header */}
          <div className="bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 dark:from-blue-900/20 dark:via-indigo-900/20 dark:to-purple-900/20 border border-indigo-200 dark:border-indigo-700 rounded-lg p-4 mb-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg">
                  <ClipboardDocumentListIcon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-800 dark:text-gray-200">{worksheet.title}</h2>
                  <p className="text-sm text-indigo-600 dark:text-indigo-400 font-medium">{worksheet.worksheetNumber}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span 
                  className="px-3 py-1.5 rounded-full text-xs font-semibold border-0 shadow-sm text-white"
                  style={{
                    backgroundColor: getStatusColorHex(worksheet.status)
                  }}
                >
                  {t(`worksheets.status.${worksheet.status}`)}
                </span>
                <span 
                  className="px-3 py-1.5 rounded-full text-xs font-semibold border-0 shadow-sm text-white"
                  style={{
                    backgroundColor: getPriorityColorHex(worksheet.priority)
                  }}
                >
                  {t(`worksheets.priority.${worksheet.priority}`)}
                </span>
              </div>
            </div>
          </div>

          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Left Column */}
            <div className="space-y-4">
              {/* Basic Info */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-3 shadow-sm">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
                  <DocumentTextIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                  Alapadatok
                </h3>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Ügyfél:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{getCustomerName(worksheet.customerId)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Helyszín:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{worksheet.location || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Határidő:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{formatDate(worksheet.dueDate) || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Munkalap dátuma:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{formatDate(worksheet.worksheetDate) || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Work Details */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3 shadow-sm">
                <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-2 flex items-center gap-1">
                  {getWorkTypeIcon(worksheet.workType)}
                  Munka részletei
                </h3>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-400">Típus:</span>
                    <span 
                      className="px-2 py-1 rounded-full text-xs font-medium border-0 text-white"
                      style={{
                        backgroundColor: getWorkTypeColorHex(worksheet.workType)
                      }}
                    >
                      {t(`worksheets.workTypes.${worksheet.workType}`)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Időtartam:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{formatTime(worksheet.startTime)} - {formatTime(worksheet.endTime)}</span>
                  </div>
                  {worksheet.description && (
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Leírás:</span>
                      <p className="font-medium mt-1 text-gray-900 dark:text-gray-100">{worksheet.description}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Travel Info */}
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border border-purple-200 dark:border-purple-700 rounded-lg p-3 shadow-sm">
                <h3 className="text-sm font-semibold text-purple-800 dark:text-purple-300 mb-2 flex items-center gap-1">
                  <TruckIcon className="h-4 w-4" />
                  Utazás
                </h3>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Kezdő km:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{worksheet.travelKmStart || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Befejező km:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{worksheet.travelKmEnd || '-'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-400">Összesen:</span>
                    <span 
                      className="px-2 py-1 rounded-full text-xs font-medium border-0 text-white"
                      style={{
                        backgroundColor: worksheet.travelKmTotal && worksheet.travelKmTotal > 0 ? '#2563eb' : '#6b7280'
                      }}
                    >
                      {worksheet.travelKmTotal || '-'} km
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-400">Költség:</span>
                    <span 
                      className="px-2 py-1 rounded-full text-xs font-medium border-0 text-white"
                      style={{
                        backgroundColor: worksheet.travelCosts && worksheet.travelCosts > 0 ? '#d97706' : '#6b7280'
                      }}
                    >
                      {worksheet.travelCosts ? `${worksheet.travelCosts} Ft` : '-'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              {/* Deployment */}
              <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border border-green-200 dark:border-green-700 rounded-lg p-3 shadow-sm">
                <h3 className="text-sm font-semibold text-green-800 dark:text-green-300 mb-2 flex items-center gap-1">
                  <TruckIcon className="h-4 w-4" />
                  Kiszállás
                </h3>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-400">Megtörtént:</span>
                    <span 
                      className="px-2 py-1 rounded-full text-xs font-medium border-0 text-white"
                      style={{
                        backgroundColor: worksheet.deploymentOccurred ? '#059669' : '#6b7280'
                      }}
                    >
                      {worksheet.deploymentOccurred ? 'Igen' : 'Nem'}
                    </span>
                  </div>
                  {worksheet.deploymentOccurred && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Dátum:</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{formatDate(worksheet.deploymentDate) || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Időpont:</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{formatTime(worksheet.deploymentTime) || '-'}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Materials & Equipment */}
              <div className="bg-gradient-to-br from-yellow-50 to-amber-100 dark:from-yellow-900/20 dark:to-amber-800/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3 shadow-sm">
                <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-2 flex items-center gap-1">
                  <WrenchScrewdriverIcon className="h-4 w-4" />
                  Anyagok & Eszközök
                </h3>
                <div className="space-y-2 text-xs">
                  {worksheet.materials && (
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Anyagok:</span>
                      <p className="font-medium mt-1 text-gray-900 dark:text-gray-100">{worksheet.materials}</p>
                    </div>
                  )}
                  {worksheet.equipment && (
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Eszközök:</span>
                      <p className="font-medium mt-1 text-gray-900 dark:text-gray-100">{worksheet.equipment}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Signatures */}
              <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 border border-indigo-200 dark:border-indigo-700 rounded-lg p-3 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-indigo-800 dark:text-indigo-300 flex items-center gap-1">
                    <PencilSquareIcon className="h-4 w-4" />
                    Aláírások
                    {worksheet.signatures && worksheet.signatures.length > 0 && (
                      <span className="px-1.5 py-0.5 bg-indigo-100 dark:bg-indigo-900/20 text-indigo-800 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-700 rounded-full text-xs font-medium ml-1">
                        {worksheet.signatures.length}
                      </span>
                    )}
                  </h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsSignatureModalOpen(true)}
                    className="text-xs h-6 px-2"
                  >
                    Hozzáad
                  </Button>
                </div>
                {worksheet.signatures && worksheet.signatures.length > 0 ? (
                  <div className="space-y-2">
                    {worksheet.signatures.map((signature, index) => (
                      <div key={index} className="bg-white dark:bg-gray-700 rounded p-2 border border-gray-200 dark:border-gray-600 relative group">
                        <button
                          onClick={() => handleRemoveSignature(signature.id)}
                          className="absolute top-1 right-1 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-colors opacity-0 group-hover:opacity-100"
                          title="Aláírás törlése"
                        >
                          <TrashIcon className="h-3 w-3" />
                        </button>
                        <div className="flex justify-between items-center text-xs pr-6">
                          <span className="font-medium text-gray-900 dark:text-gray-100">{signature.signerName}</span>
                          <span className="text-gray-500 dark:text-gray-400">{signature.signerRole}</span>
                        </div>
                        <img 
                          src={signature.signatureData} 
                          alt="Aláírás" 
                          className="w-full h-12 object-contain bg-white dark:bg-gray-800 rounded mt-1"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {formatDateTime(signature.signedAt)}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-2">Nincsenek aláírások</p>
                )}
              </div>
            </div>
          </div>

          {/* Special Notes */}
          {worksheet.specialNotes && (
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-3 mt-4 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
                <ExclamationTriangleIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                Speciális megjegyzések
              </h3>
              <p className="text-xs text-gray-900 dark:text-gray-100">{worksheet.specialNotes}</p>
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-between items-center mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
            <Button 
              variant="outline" 
              onClick={handlePrint} 
              size="sm"
              className="bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/30"
            >
              <PrinterIcon className="h-4 w-4 mr-1" />
              Nyomtat
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} size="sm">
                Bezárás
              </Button>
              {onEdit && (
                <Button onClick={() => onEdit(worksheet)} size="sm">
                  <PencilSquareIcon className="h-4 w-4 mr-1" />
                  Szerkesztés
                </Button>
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Signature Modal */}
      {isSignatureModalOpen && (
        <DigitalSignature
          isOpen={isSignatureModalOpen}
          onClose={() => setIsSignatureModalOpen(false)}
          onSave={handleSignature}
          title={t('worksheets.actions.addSignature')}
        />
      )}
    </>
  );
};

export default WorksheetView; 